/**
 * Prediction Filtering Utilities
 *
 * This module provides semantic functions for filtering and deduplicating predictions
 * from inference results. It's designed to handle the complex logic of:
 *
 * 1. Confidence-based filtering - Remove predictions below a threshold
 * 2. NMS deduplication - Remove overlapping predictions within results
 * 3. Cross-class deduplication - Remove predictions that overlap with existing annotations
 * 4. Fallback prediction - When no new predictions found, try lower-confidence ones
 *
 * The main entry point is `smartFilterPredictions()` which orchestrates all these steps.
 *
 * @see CLAUDE.md in the hooks directory for architectural context
 */

import { applyNMS, crossClassNMSWithExisting } from "./nms";
import type { Annotation } from "@rapid/types/annotation";

// ============================================================================
// Types
// ============================================================================

export interface Prediction {
    x: number;
    y: number;
    width: number;
    height: number;
    confidence: number;
    class?: string;
    label?: string;
    [key: string]: any;
}

export interface SmartFilterOptions {
    /**
     * Confidence threshold - predictions below this are initially filtered out.
     * If not provided, no confidence filtering is applied.
     */
    confidenceThreshold?: number;

    /**
     * IoU threshold for NMS - predictions overlapping more than this are deduplicated.
     * Defaults to 0.5 (50% overlap).
     */
    iouThreshold?: number;

    /**
     * When true and no new predictions are found above threshold,
     * include the highest-confidence prediction below threshold.
     * This enables the "keep clicking to find more" behavior.
     */
    fallbackBelowThreshold?: boolean;

    /**
     * For single-label mode: the target label to filter for.
     * Required when fallbackBelowThreshold is true.
     */
    targetLabel?: string;

    /**
     * Existing annotations to deduplicate against.
     * New predictions overlapping with these are removed.
     */
    existingAnnotations?: Annotation[];
}

export interface SmartFilterResult {
    /** The filtered predictions ready for use */
    predictions: Prediction[];

    /** Whether a fallback (below-threshold) prediction was used */
    usedFallback: boolean;

    /** Debug info about the filtering process */
    debug: {
        inputCount: number;
        afterConfidenceFilter: number;
        afterNMS: number;
        afterDedup: number;
        fallbackConfidence?: number;
    };
}

// ============================================================================
// Core Filtering Functions
// ============================================================================

/**
 * Filters predictions by confidence threshold.
 *
 * @param predictions - Raw predictions from inference
 * @param threshold - Minimum confidence to keep (0-1)
 * @returns Predictions with confidence >= threshold
 */
export function filterByConfidence<T extends Prediction>(predictions: T[], threshold: number): T[] {
    return predictions.filter((p) => p.confidence >= threshold);
}

/**
 * Applies Non-Maximum Suppression to remove overlapping predictions.
 * Keeps the highest-confidence prediction when boxes overlap significantly.
 *
 * @param predictions - Predictions to deduplicate
 * @param iouThreshold - IoU threshold above which boxes are considered duplicates
 * @returns Deduplicated predictions (highest confidence wins)
 */
export function deduplicateWithNMS<T extends Prediction>(
    predictions: T[],
    iouThreshold: number = 0.5
): T[] {
    return applyNMS(predictions, iouThreshold);
}

/**
 * Removes predictions that overlap with existing annotations.
 * Uses cross-class NMS to handle cases where different classes detect the same object.
 *
 * @param predictions - New predictions to filter
 * @param existingAnnotations - Annotations already on the canvas
 * @param iouThreshold - IoU threshold for considering boxes as duplicates
 * @returns Object with kept predictions and IDs of annotations to remove (if new pred has higher confidence)
 */
export function deduplicateAgainstExisting<T extends Prediction>(
    predictions: T[],
    existingAnnotations: Annotation[],
    iouThreshold: number = 0.5
): { keptPredictions: T[]; annotationIdsToRemove: string[] } {
    if (existingAnnotations.length === 0) {
        return { keptPredictions: predictions, annotationIdsToRemove: [] };
    }

    return crossClassNMSWithExisting(predictions, existingAnnotations, iouThreshold);
}

/**
 * Finds the best fallback prediction when no new predictions were found above threshold.
 *
 * This handles the "Find More Objects" use case where the user keeps clicking
 * but we've already found everything above the confidence threshold. Rather than
 * showing nothing, we surface the next-best prediction below threshold.
 *
 * @param allPredictions - All predictions (including below threshold)
 * @param confidenceThreshold - The threshold that was used for filtering
 * @param existingAnnotations - Annotations to check for duplicates
 * @param targetLabel - The label we're looking for
 * @param iouThreshold - IoU threshold for duplicate detection
 * @returns The best fallback prediction, or null if none found
 */
export function findFallbackPrediction<T extends Prediction>(
    allPredictions: T[],
    confidenceThreshold: number,
    existingAnnotations: Annotation[],
    targetLabel: string,
    iouThreshold: number = 0.5
): T | null {
    // Get predictions below threshold, sorted by confidence (highest first)
    const belowThresholdPredictions = allPredictions
        .filter((p) => p.confidence < confidenceThreshold)
        .sort((a, b) => b.confidence - a.confidence);

    // Filter existing annotations to only the target label
    const targetAnnotations = existingAnnotations.filter((a) => a.label === targetLabel);

    // Find the first below-threshold prediction that's not a duplicate
    for (const pred of belowThresholdPredictions) {
        const { keptPredictions } = crossClassNMSWithExisting(
            [pred],
            targetAnnotations,
            iouThreshold
        );

        if (keptPredictions.length > 0) {
            return pred;
        }
    }

    return null;
}

// ============================================================================
// Main Entry Point
// ============================================================================

/**
 * Smart prediction filtering - the main entry point for filtering inference results.
 *
 * This function orchestrates the complete filtering pipeline:
 *
 * 1. **Confidence Filter**: Remove predictions below the threshold
 * 2. **NMS Deduplication**: Remove overlapping predictions within results
 * 3. **Existing Deduplication**: Remove predictions that duplicate existing annotations
 * 4. **Fallback (optional)**: If no new predictions found and fallback enabled,
 *    find the highest-confidence prediction below threshold that's not a duplicate
 *
 * The fallback behavior enables the "Find More Objects" button to progressively
 * show lower-confidence predictions when the user keeps clicking, rather than
 * showing nothing after all high-confidence objects are found.
 *
 * @example
 * ```typescript
 * const result = smartFilterPredictions(rawPredictions, {
 *     confidenceThreshold: 0.8,
 *     fallbackBelowThreshold: true,
 *     targetLabel: 'person',
 *     existingAnnotations: currentAnnotations
 * });
 *
 * if (result.usedFallback) {
 *     console.log(`Found fallback at ${result.debug.fallbackConfidence} confidence`);
 * }
 * ```
 *
 * @param predictions - Raw predictions from inference
 * @param options - Filtering options
 * @returns Filtered predictions with debug info
 */
export function smartFilterPredictions<T extends Prediction>(
    predictions: T[],
    options: SmartFilterOptions = {}
): SmartFilterResult {
    const {
        confidenceThreshold,
        iouThreshold = 0.5,
        fallbackBelowThreshold = false,
        targetLabel,
        existingAnnotations = []
    } = options;

    const debug = {
        inputCount: predictions.length,
        afterConfidenceFilter: 0,
        afterNMS: 0,
        afterDedup: 0,
        fallbackConfidence: undefined as number | undefined
    };

    // Step 1: Filter by confidence threshold
    let filtered: T[] = confidenceThreshold
        ? filterByConfidence(predictions, confidenceThreshold)
        : predictions;
    debug.afterConfidenceFilter = filtered.length;

    // Step 2: Apply NMS to remove overlapping predictions
    filtered = deduplicateWithNMS(filtered, iouThreshold);
    debug.afterNMS = filtered.length;

    // Step 3: Deduplicate against existing annotations
    const { keptPredictions } = deduplicateAgainstExisting(
        filtered,
        existingAnnotations,
        iouThreshold
    );
    debug.afterDedup = keptPredictions.length;

    // Step 4: Fallback logic - if no NEW predictions found above threshold,
    // try to find at least one below threshold
    let usedFallback = false;
    let finalPredictions = keptPredictions;

    if (fallbackBelowThreshold && confidenceThreshold && targetLabel && predictions.length > 0) {
        // Check if we found any NEW predictions (not duplicates of existing)
        // If keptPredictions is empty, we didn't find anything new above threshold
        if (keptPredictions.length === 0) {
            const targetAnnotations = existingAnnotations.filter((a) => a.label === targetLabel);
            const fallback = findFallbackPrediction(
                predictions,
                confidenceThreshold,
                targetAnnotations,
                targetLabel,
                iouThreshold
            );

            if (fallback) {
                usedFallback = true;
                debug.fallbackConfidence = fallback.confidence;
                finalPredictions = [fallback] as T[];
            }
        }
    }

    return { predictions: finalPredictions as T[], usedFallback, debug };
}
